import type { TCourseViewModel, TTreeViewModel } from '../../services/models/CourseViewModels';

/**
 * The course page tree flattened into the rows the wizard sidebar draws while the AI builds a
 * course. Presentation shaping only — nothing here decides how far generation has got, which is
 * read from macro state.
 */

export type TAiSessionPagePreviewStatus = 'created' | 'thumbnail-ready';

export interface IAiSessionPagePreview {
  id: string;
  coursePageId: number;
  sortIndex: number;
  depth: number;
  title: string;
  status: TAiSessionPagePreviewStatus;
  thumbnailUrl?: string;
}

export interface IAiSessionCoursePreview {
  id: string;
  courseId: number;
  title: string;
  description?: string;
  pages: IAiSessionPagePreview[];
}

export const flattenPageTreeWithDepth = (pages: TTreeViewModel[], depth = 0): { node: TTreeViewModel; depth: number }[] =>
  pages.flatMap((page) => [{ node: page, depth }, ...flattenPageTreeWithDepth(page.children ?? [], depth + 1)]);

export const resolvePageThumbnailUrl = (avatarValue: string | undefined, baseURL = process.env.REACT_APP_ENV_BASEURL): string | undefined => {
  const normalized = typeof avatarValue === 'string' ? avatarValue.trim() : '';
  if (!normalized || normalized === 'false') return undefined;

  const resolved = normalized.includes('https') ? normalized : `https://${baseURL}.norskinteraktiv.no/niasAPI${normalized}`;
  if (resolved.toLowerCase().includes('defaultcourseavatar')) return undefined;

  return resolved;
};

export const mapAiSessionCoursePreview = (course: TCourseViewModel): IAiSessionCoursePreview => {
  const pages: IAiSessionPagePreview[] = flattenPageTreeWithDepth(course.pages ?? []).map(({ node, depth }, index) => {
    const thumbnailUrl = resolvePageThumbnailUrl(node.data?.avatar?.value);

    return {
      id: `page-${node.id}`,
      coursePageId: Number(node.id),
      sortIndex: index,
      depth,
      title: node.data?.title?.value || node.data?.name || `Page ${index + 1}`,
      status: thumbnailUrl ? 'thumbnail-ready' : 'created',
      thumbnailUrl,
    };
  });

  return {
    id: `course-${course.courseID}`,
    courseId: course.courseID,
    title: course.title,
    description: course.description,
    pages,
  };
};
